/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** localStorage key of the desktop sidebar collapsed state. */
export const SIDEBAR_COLLAPSED_KEY = "avante.desk.sidebar.collapsed";
const TOGGLE_SHORTCUT = "b";

interface DeskShellValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

const DeskShellContext = createContext<DeskShellValue | null>(null);

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Collapsed state of the desktop sidebar: persisted in localStorage and
 * toggled with Ctrl/Cmd+B (the shortcut of the previous shadcn sidebar).
 */
export const DeskShellProvider = ({ children }: { children: ReactNode }) => {
  const [collapsed, setCollapsedState] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // storage unavailable (private mode): the state just isn't persisted
    }
  }, [collapsed]);

  const setCollapsed = useCallback(
    (next: boolean) => setCollapsedState(next),
    [],
  );
  const toggle = useCallback(() => setCollapsedState((prev) => !prev), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === TOGGLE_SHORTCUT &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const value = useMemo(
    () => ({ collapsed, setCollapsed, toggle }),
    [collapsed, setCollapsed, toggle],
  );
  return (
    <DeskShellContext.Provider value={value}>
      {children}
    </DeskShellContext.Provider>
  );
};

export const useDeskShell = (): DeskShellValue => {
  const value = useContext(DeskShellContext);
  if (!value) throw new Error("useDeskShell must be used in DeskShellProvider");
  return value;
};
